<template>
    <!-- Editions Divider -->
    <divider-generic
    icon="pencil"
    :title="t('approvals.operators.operatorDetails.editionsBlock.editionsTitle')"/>

    <!-- Name changes -->
    <div v-if="nameChanged" class="groups-wrapper">
        <div class="edition-label">{{t('approvals.operators.operatorDetails.editionsBlock.editionNameLabel')}}</div>
        <div class="edition-value">- {{nameChangedText}}</div>
    </div>

    <!-- Email changes -->
    <div v-if="emailChanged" class="groups-wrapper">
        <div class="edition-label">{{t('approvals.operators.operatorDetails.editionsBlock.editionEmailLabel')}}</div>
        <div class="edition-value">- {{emailChangedText}}</div>
    </div>

    <!-- Msisdn changes -->
    <div v-if="msisdnChanged" class="groups-wrapper">
        <div class="edition-label">{{t('approvals.operators.operatorDetails.editionsBlock.editionMsisdnLabel')}}</div>
        <div class="edition-value">- {{msisdnChangedText}}</div>
    </div>

    <!-- Nif changes -->
    <div v-if="nifChanged" class="groups-wrapper">
        <div class="edition-label">{{t('approvals.operators.operatorDetails.editionsBlock.editionNifLabel')}}</div>
        <div class="edition-value">- {{nifChangedText}}</div>
    </div>

    <!-- Profile changes -->
    <div v-if="profileChanged" class="groups-wrapper">
        <div class="edition-label">{{t('approvals.operators.operatorDetails.editionsBlock.editionProfileLabel')}}</div>
        <div class="edition-value">- {{profileChangeText}}</div>
    </div>

    <!-- Weight changes -->
    <div v-if="weightChanged" class="groups-wrapper">
        <div class="edition-label">{{t('approvals.operators.operatorDetails.editionsBlock.editionWeightLabel')}}</div>
        <div class="edition-value">- {{weightChangeText}}</div>
    </div>

    <!-- Status changes -->
    <div v-if="statusChanged" class="groups-wrapper">
        <div
        v-if="operatorDetails.statusId === 2 && operatorDetails.pendingInfoToUpdate.statusId === 4"
        class="edition-label">
            {{t('approvals.operators.operatorDetails.editionsBlock.editionBlockLabel')}}
        </div>
        <div v-else	class="edition-label">
            {{t('approvals.operators.operatorDetails.editionsBlock.editionUnblockLabel')}}
        </div>
        <div class="edition-value">{{statusChangeText}}</div>
    </div>

    <!-- Accounts Access changes-->
    <div v-if="newAccountsList.length || removedAccounts.length" class="groups-wrapper">
        <div class="edition-label">{{t('approvals.operators.operatorDetails.editionsBlock.accountsAccessLabel')}}</div>
        <!-- New Accounts editions -->
        <div
        v-for="(newAccount, index) in newAccountsList"
        :key="`newAccount-${index}`"
        class="edition-value">- {{newAccount}}</div>
        <!-- Removed Accounts editions -->
        <div
        v-for="(removedAccount, index) in removedAccounts"
        :key="`removedAccount-${index}`"
        class="edition-value">- {{removedAccount}}</div>
    </div>

    <!-- Operations Access changes -->
    <div v-if="newOperationsAccessList.length || oldOperationsAccessList.length" class="groups-wrapper">
        <div class="edition-label">{{t('approvals.operators.operatorDetails.editionsBlock.operationsAccessLabel')}}</div>
        <!-- New operations Access -->
        <div
        v-for="(newOperation, index) in newOperationsAccessList"
        :key="`newOperation-${index}`"
        class="edition-value">- {{newOperation}}</div>
        <!-- Removed operations access -->
        <div
        v-for="(oldOperation, index) in oldOperationsAccessList"
        :key="`oldOperation-${index}`"
        class="edition-value">- {{oldOperation}}</div>
    </div>

    <!-- Plafond changes -->
    <div v-if="operationsPlafondsChangedList.length" class="groups-wrapper">
        <div class="edition-label">{{t('approvals.operators.operatorDetails.editionsBlock.plafondEditionLabel')}}</div>
        <div
        v-for="(newPlafond, index) in operationsPlafondsChangedList"
        :key="`newPlafond-${index}`"
        class="edition-value">- {{newPlafond}}</div>
    </div>

    <!-- Limits changes -->
    <div v-if="operationsLimitsChangedList.length" class="groups-wrapper">
        <div class="edition-label">{{t('approvals.operators.operatorDetails.editionsBlock.limitEditionLabel')}}</div>
        <div
        v-for="(newLimit, index) in operationsLimitsChangedList"
        :key="`newLimit-${index}`"
        class="edition-value">- {{newLimit}}</div>
    </div>
</template>

<script setup>
import DividerGeneric from '@/components/generic/dividers/Generic'

/* Vue & Helpers */
import { ref, computed } from 'vue'
import { amountFormat } from '@/assets/scripts/amounts'
import { accountFormatHelper } from '@/assets/scripts/strings'

/* i18n */
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

const props = defineProps({
    operatorDetails: Object,
    accounts: Array,
    operationTypesList: Array
})

/* Data */
const newAccountsList = ref([])
const newOperationsAccessList = ref([])
const oldOperationsAccessList = ref([])
const operationsPlafondsChangedList = ref([])
const operationsLimitsChangedList = ref([])

/*************************************************
*                                                *
*                     Auxs                       *
*                                                *
*************************************************/
const getAccountNumber = accountId => {
    return props.accounts.bankAccountView.find(obj => obj.id === parseInt(accountId)).accountNumber
}
const getAccountCurrency = accountId => {
    return props.accounts.bankAccountView.find(obj => obj.id === parseInt(accountId)).currency
}
const getOperationDescription = operationTypeId => {
    return props.operationTypesList.find(obj => obj.id === parseInt(operationTypeId)).type
}

/*************************************************
*                                                *
*                     Name                       *
*                                                *
*************************************************/
const nameChanged = computed(() => {
    return props.operatorDetails.name && props.operatorDetails.name !== props.operatorDetails.pendingInfoToUpdate.name
})
const nameChangedText = computed(() => {
    return t('approvals.operators.operatorDetails.editionsBlock.nameChanged', {
        previousName: props.operatorDetails.name,
        newName: props.operatorDetails.pendingInfoToUpdate.name
    })
})

/*************************************************
*                                                *
*                     Email                      *
*                                                *
*************************************************/
const emailChanged = computed(() => {
    return props.operatorDetails.email && props.operatorDetails.email !== props.operatorDetails.pendingInfoToUpdate.email
})
const emailChangedText = computed(() => {
    return t('approvals.operators.operatorDetails.editionsBlock.emailChanged', {
        previousEmail: props.operatorDetails.email,
        newEmail: props.operatorDetails.pendingInfoToUpdate.email
    })
})

/*************************************************
*                                                *
*                    MSISDN                      *
*                                                *
*************************************************/
const msisdnChanged = computed(() => {
    return props.operatorDetails.msisdn && props.operatorDetails.msisdn !== props.operatorDetails.pendingInfoToUpdate.msisdn
})
const msisdnChangedText = computed(() => {
    return t('approvals.operators.operatorDetails.editionsBlock.msisdnChanged', {
        previousMsisdn: props.operatorDetails.msisdn,
        newMsisdn: props.operatorDetails.pendingInfoToUpdate.msisdn
    })
})

/*************************************************
*                                                *
*                      NIF                       *
*                                                *
*************************************************/
const nifChanged = computed(() => {
    return props.operatorDetails.nif !== props.operatorDetails.pendingInfoToUpdate.nif
})
const nifChangedText = computed(() => {
    return t('approvals.operators.operatorDetails.editionsBlock.nifChanged', {
        previousNif: props.operatorDetails.nif,
        newNif: props.operatorDetails.pendingInfoToUpdate.nif
    })
})

/*************************************************
*                                                *
*                   Profile                      *
*                                                *
*************************************************/
const profileChanged = computed(() => {
    return props.operatorDetails.profileId && props.operatorDetails.profileId !== props.operatorDetails.pendingInfoToUpdate.profileId
})
const profileChangeText = computed(() => {
    return t('approvals.operators.operatorDetails.editionsBlock.profileChanged', {
        previousRole: props.operatorDetails.profile?.operatorProfileDescription || '-',
        newRole: props.operatorDetails.pendingInfoToUpdate.profileDescription
    })
})

/*************************************************
*                                                *
*                     Weight                     *
*                                                *
*************************************************/
const weightChanged = computed(() => {
    return props.operatorDetails.weight !== props.operatorDetails.pendingInfoToUpdate.weight && ((props.operatorDetails.weight && props.operatorDetails.weight !== 0) || (props.operatorDetails.pendingInfoToUpdate.weight && props.operatorDetails.pendingInfoToUpdate.weight !== 0))
})
const weightChangeText = computed(() => {
    return t('approvals.operators.operatorDetails.editionsBlock.weightChanged', {
        previousWeight: props.operatorDetails.weight ? props.operatorDetails.weight : 0,
        newWeight: props.operatorDetails.pendingInfoToUpdate.weight ? props.operatorDetails.pendingInfoToUpdate.weight : 0
    })
})

/*************************************************
*                                                *
*                    Status                      *
*                                                *
*************************************************/
const statusChanged = computed(() => {
    return props.operatorDetails.statusId && props.operatorDetails.pendingInfoToUpdate.statusId && (props.operatorDetails.statusId !== props.operatorDetails.pendingInfoToUpdate.statusId)
})
const statusChangeText = computed(() => {
    if (props.operatorDetails.statusId === 2 && props.operatorDetails.pendingInfoToUpdate.statusId === 4) {
        return t('approvals.operators.operatorDetails.editionsBlock.statusChangedToBlocked')
    } else {
        return t('approvals.operators.operatorDetails.editionsBlock.statusChangedToActive')
    }
})

/*************************************************
*                                                *
*                    Accounts                    *
*                                                *
*************************************************/
const addingNewAccounts = () => {
    const currentConfig = props.operatorDetails.accountAccessConfig
    const newConfig = props.operatorDetails.pendingInfoToUpdate.accountAccessConfig

    for (const newAccount in newConfig) {
        const newAccountConfig = newConfig[newAccount]
        /* Validating if this account already existed, if new enters validiton */
        if (!currentConfig[newAccount]) {
            /* Adding account to new accounts array */
            newAccountsList.value.push(t('approvals.operators.operatorDetails.editionsBlock.addedAccount', {
                accountNumber: accountFormatHelper(getAccountNumber(newAccount))
            }))
            /* Adding operation to new operations array */
            const newOperationLimits = newAccountConfig.operationLimits
            for (const newOperation in newOperationLimits) {
                newOperationsAccessList.value.push(t('approvals.operators.operatorDetails.editionsBlock.addOperationToAccount', {
                    accountNumber: accountFormatHelper(getAccountNumber(newAccount)),
                    operation: getOperationDescription(newOperation)
                }))
                /* Adding new plafond to new operationsPlafondsChangedList */
                operationsPlafondsChangedList.value.push(t('approvals.operators.operatorDetails.editionsBlock.newOperationPlafondToAccount', {
                    accountNumber: accountFormatHelper(getAccountNumber(newAccount)),
                    operation: getOperationDescription(newOperation),
                    newAmount: amountFormat(newOperationLimits[newOperation].dailyPlafond, getAccountCurrency(newAccount))
                }))
                /* Adding new limit to new operationsLimitsChangedList */
                operationsLimitsChangedList.value.push(t('approvals.operators.operatorDetails.editionsBlock.newOperationLimitToAccount', {
                    accountNumber: accountFormatHelper(getAccountNumber(newAccount)),
                    operation: getOperationDescription(newOperation),
                    newAmount: amountFormat(newOperationLimits[newOperation].transactionLimit, getAccountCurrency(newAccount))
                }))
            }
        }
    }
}

const removedAccounts = computed(() => {
    const removedAccounts = []

    props.operatorDetails.accountIds.forEach(account => {
        if (!props.operatorDetails.pendingInfoToUpdate.accountIds.find(obj => obj === account)) {
            removedAccounts.push(t('approvals.operators.operatorDetails.editionsBlock.removedAccount', {
                accountNumber: accountFormatHelper(getAccountNumber(account))
            }))
        }
    })

    return removedAccounts
})

/*************************************************
*                                                *
*                  Operations                    *
*                                                *
*************************************************/
const newOperationsAccess = () => {
    const currentConfig = props.operatorDetails.accountAccessConfig
    const newConfig = props.operatorDetails.pendingInfoToUpdate.accountAccessConfig
    if (!newConfig) return
    /* First we will check our currentConfig to know:
    - if some account was removed,
    - if it's operations changed,
    - operations plafonds and limits changed */
    for (const account in currentConfig) {
        /* Validating if this account still exists */
        if (newConfig[account]) {
            const newAccountConfig = newConfig[account]
            /* Checking if operation limit still exists */
            const operationLimits = currentConfig[account].operationLimits
            const newOperationLimits = newAccountConfig.operationLimits

            for (const operation in operationLimits) {
                /* here we will create changes in operations that already existed */
                if (newOperationLimits[operation]) {
                    /* Validating if operation plafond changed */
                    if (newOperationLimits[operation].dailyPlafond !== operationLimits[operation].dailyPlafond) {
                        operationsPlafondsChangedList.value.push(t('approvals.operators.operatorDetails.editionsBlock.changedOperationPlafondToAccount', {
                            accountNumber: accountFormatHelper(getAccountNumber(account)),
                            operation: getOperationDescription(operation),
                            oldAmount: amountFormat(operationLimits[operation].dailyPlafond, getAccountCurrency(account)),
                            newAmount: amountFormat(newOperationLimits[operation].dailyPlafond, getAccountCurrency(account))
                        }))
                    }
                        /* Validating if operation limit changed */
                    if (newOperationLimits[operation].transactionLimit !== operationLimits[operation].transactionLimit) {
                        operationsPlafondsChangedList.value.push(t('approvals.operators.operatorDetails.editionsBlock.changedOperationLimitToAccount', {
                            accountNumber: accountFormatHelper(getAccountNumber(account)),
                            operation: getOperationDescription(operation),
                            oldAmount: amountFormat(operationLimits[operation].transactionLimit, getAccountCurrency(account)),
                            newAmount: amountFormat(newOperationLimits[operation].transactionLimit, getAccountCurrency(account))
                        }))
                    }
                } else {
                    // operation was removed
                    oldOperationsAccessList.value.push(t('approvals.operators.operatorDetails.editionsBlock.removeOperationToAccount', {
                        accountNumber: accountFormatHelper(getAccountNumber(account)),
                        operation: getOperationDescription(operation)
                    }))
                }
            }

            for (const newOperation in newOperationLimits) {
                /* here we will add new operations that didnt existed before */
                if (!operationLimits[newOperation]) {
                    newOperationsAccessList.value.push(t('approvals.operators.operatorDetails.editionsBlock.addOperationToAccount', {
                        accountNumber: accountFormatHelper(getAccountNumber(account)),
                        operation: getOperationDescription(newOperation)
                    }))
                    /* Adding new plafond to new operationsPlafondsChangedList */
                    operationsPlafondsChangedList.value.push(t('approvals.operators.operatorDetails.editionsBlock.newOperationPlafondToAccount', {
                        accountNumber: accountFormatHelper(getAccountNumber(account)),
                        operation: getOperationDescription(newOperation),
                        newAmount: amountFormat(newOperationLimits[newOperation].dailyPlafond, getAccountCurrency(account))
                    }))
                    /* Adding new limit to new operationsLimitsChangedList */
                    operationsLimitsChangedList.value.push(t('approvals.operators.operatorDetails.editionsBlock.newOperationLimitToAccount', {
                        accountNumber: accountFormatHelper(getAccountNumber(account)),
                        operation: getOperationDescription(newOperation),
                        newAmount: amountFormat(newOperationLimits[newOperation].transactionLimit, getAccountCurrency(account))
                    }))
                }
            }
        }
    }
    /* LOOKING FOR NEW ACCOUNTS */
    addingNewAccounts()
}

newOperationsAccess()
</script>

<style lang="scss" scoped>
.groups-wrapper {
    width: 100%;

    .edition-label {
        height: 16px;
        margin-bottom: 0;
        display: block;

        @include H7-Regular;
        line-height: 16px;
        color: var(--secondary-dark-1);

        white-space: nowrap;
        text-overflow: ellipsis;
        overflow: hidden;
    }

    .edition-value {
        width: 100%;
        min-height: 24px;

        @include H6-Regular;
        line-height: 18px;
        color: var(--secondary-dark-2);

        text-overflow:clip;
        white-space:normal;
    }
}
</style>
